import { z } from 'zod'

/**
 * Shared offset pagination primitive for list endpoints (see docs/tasks/TASK_RUNNERS.md).
 * Queries fetch `count + 1` rows and pass them to `toPage` to compute `more`.
 * Named *.contract.ts because oRPC contracts import it, so it crosses into the web
 * bundle (fail-on-server-imports guard) — keep it dependency-free beyond zod.
 */
export type PageRequest = {
  offset: number
  count: number
}

export type Page = {
  offset: number // echoes the request
  size: number // number of rows actually returned
  more: boolean // are there rows beyond offset + size
}

export const pageRequestSchema = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(1).max(100).default(25),
})

export const pageSchema = z.object({
  offset: z.number(),
  size: z.number(),
  more: z.boolean(),
})

/**
 * Build the `{ page, data }` envelope from a query that fetched `count + 1` rows
 * (the extra row detects `more` and is trimmed from the result).
 */
export function toPage<T>(request: PageRequest, rowsPlusOne: T[]): { page: Page; data: T[] } {
  const data = rowsPlusOne.slice(0, request.count)
  return {
    page: { offset: request.offset, size: data.length, more: rowsPlusOne.length > request.count },
    data,
  }
}
